'use client';

import { cn } from 'cn';
import { Dialog as SheetPrimitive } from 'radix-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { XIcon } from 'lucide-react';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

/*
 * `forwardRef`: see the note in input.tsx — upstream targets React 19. Radix
 * clones this overlay with a ref (it composes it with the dismissable-*
 * layer registration), so a plain function component silently drops it.
 */
const SheetOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof SheetPrimitive.Overlay>
>(function SheetOverlay({ className, ...props }, ref) {
  return (
    <SheetPrimitive.Overlay
      ref={ref}
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
});

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  resizable = false,
  defaultWidth = 960,
  minWidth = 480,
  maxWidth = 1600,
  resizeStorageKey,
  style,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  showCloseButton?: boolean;
  /** Let a right-hand sheet be resized by dragging its leading edge. */
  resizable?: boolean;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  /** Remember the reader's width under this key. */
  resizeStorageKey?: string;
}) {
  const { t } = useTranslation();
  const [width, setWidth] = React.useState(() => {
    if (!resizeStorageKey || typeof window === 'undefined') return defaultWidth;
    try {
      const stored = Number(window.localStorage.getItem(resizeStorageKey));
      return Number.isFinite(stored) && stored > 0 ? stored : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });
  const widthRef = React.useRef(width);
  widthRef.current = width;
  const dragRef = React.useRef<{ startX: number; startWidth: number } | null>(null);
  const [resizing, setResizing] = React.useState(false);

  React.useEffect(() => {
    if (!resizable || !resizeStorageKey || typeof window === 'undefined') return;
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(resizeStorageKey, String(Math.round(width)));
      } catch {
        // A blocked storage API must not make the drawer unusable.
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, [resizable, resizeStorageKey, width]);

  React.useEffect(() => {
    if (!resizable || typeof window === 'undefined') return;
    const clampToViewport = () => {
      const visibleMax = Math.max(minWidth, Math.min(maxWidth, window.innerWidth - 24));
      setWidth((current) => Math.min(visibleMax, Math.max(minWidth, current)));
    };
    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, [maxWidth, minWidth, resizable]);

  React.useEffect(() => {
    if (!resizing || typeof window === 'undefined') return;
    const maxVisibleWidth = () => Math.max(minWidth, Math.min(maxWidth, window.innerWidth - 24));
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const next = drag.startWidth + drag.startX - event.clientX;
      setWidth(Math.min(maxVisibleWidth(), Math.max(minWidth, next)));
    };
    const stop = () => {
      dragRef.current = null;
      setResizing(false);
    };
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [maxWidth, minWidth, resizing]);

  const maxVisibleWidth = () =>
    typeof window === 'undefined'
      ? maxWidth
      : Math.max(minWidth, Math.min(maxWidth, window.innerWidth - 24));
  const setClampedWidth = (next: number) =>
    setWidth(Math.min(maxVisibleWidth(), Math.max(minWidth, next)));
  const beginResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startWidth: widthRef.current };
    setResizing(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const resizeWithKeyboard = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 64 : 24;
    const next =
      event.key === 'ArrowLeft'
        ? width + step
        : event.key === 'ArrowRight'
          ? width - step
          : event.key === 'Home'
            ? minWidth
            : event.key === 'End'
              ? maxVisibleWidth()
              : null;
    if (next === null) return;
    event.preventDefault();
    setClampedWidth(next);
  };

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          'fixed z-50 flex flex-col gap-4 bg-popover bg-clip-padding text-sm text-popover-foreground shadow-lg transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-[side=bottom]:data-open:slide-in-from-bottom-10 data-[side=left]:data-open:slide-in-from-left-10 data-[side=right]:data-open:slide-in-from-right-10 data-[side=top]:data-open:slide-in-from-top-10 data-closed:animate-out data-closed:fade-out-0 data-[side=bottom]:data-closed:slide-out-to-bottom-10 data-[side=left]:data-closed:slide-out-to-left-10 data-[side=right]:data-closed:slide-out-to-right-10 data-[side=top]:data-closed:slide-out-to-top-10',
          resizing && 'transition-none',
          className,
        )}
        style={
          resizable
            ? {
                width: `min(${width}px, calc(100vw - 24px))`,
                maxWidth: 'none',
                ...style,
              }
            : style
        }
        {...props}
      >
        {resizable && side === 'right' && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={t('common.resize_panel', 'Resize panel')}
            aria-valuemin={minWidth}
            aria-valuemax={Math.round(maxVisibleWidth())}
            aria-valuenow={Math.round(width)}
            tabIndex={0}
            title={t('common.resize_panel_hint', 'Drag to resize, double-click to reset')}
            onPointerDown={beginResize}
            onKeyDown={resizeWithKeyboard}
            onDoubleClick={() => setClampedWidth(defaultWidth)}
            className={cn(
              'absolute inset-y-0 left-0 z-20 w-3 -translate-x-1/2 cursor-col-resize touch-none bg-transparent focus-visible:outline-none',
              'after:absolute after:top-1/2 after:left-1/2 after:h-10 after:w-0.5 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-transparent after:transition-colors',
              'hover:after:bg-brand/70 focus-visible:after:bg-brand',
              resizing && 'after:bg-brand',
            )}
          />
        )}
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close data-slot="sheet-close" asChild>
            <Button variant="ghost" className="absolute top-3 right-3" size="icon-sm">
              <XIcon />
              <span className="sr-only">Close</span>
            </Button>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-0.5 p-4', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-heading text-base font-medium text-foreground', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
